import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlClientUserInviteInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useClientUserInvite from "../../api/useClientUserCreate";
import useRequireCurrentClient from "../../hooks/useRequireCurrentClient";

type Props = {
    onSubmitted: () => void;
};

export default function CreateClientUserForm({ onSubmitted }: Props) {
    const { t } = useTranslation("user");
    const { client } = useRequireCurrentClient();
    const { inviteClientUser, error } = useClientUserInvite();
    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isSubmitting, isDirty, isSubmitted, isLoading },
    } = useForm<Omit<GqlClientUserInviteInput, "clientId">>({
        values: { email: "" },
    });

    const onSubmit: SubmitHandler<Omit<GqlClientUserInviteInput, "clientId">> = async ({ email }) => {
        if (!client) {
            return;
        }
        await inviteClientUser({ clientId: client.id, email });
        onSubmitted();
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    label={t("userCreateForm.labelEmail")}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register("email", {
                        required: t("userCreateForm.emailRequired"),
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: t("userCreateForm.emailInvalid"),
                        },
                    })}
                />

                <Button
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty}
                    loading={isSubmitting || isLoading}
                >
                    {t("userCreateForm.sendInvite")}
                </Button>
            </Stack>
        </form>
    );
}
